import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'

type AvatarProps = {
  src?: string | null
  square?: boolean
  initials?: string
  alt?: string
  className?: string
  slot?: string
}

export function Avatar({
  src = null,
  square = false,
  initials,
  alt = '',
  className,
  slot,
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot={slot || "avatar"}
      {...props}
      className={clsx(
        className,
        // Basic layout
        'inline-grid shrink-0 align-middle [--avatar-radius:20%] [--ring-opacity:20%] *:col-start-1 *:row-start-1',
        'outline -outline-offset-1 outline-black/(--ring-opacity) dark:outline-white/(--ring-opacity)',
        // Add the correct border radius
        square ? 'rounded-(--avatar-radius) *:rounded-(--avatar-radius)' : 'rounded-full *:rounded-full'
      )}
    >
      {initials && (
        <svg
          className="size-full fill-current p-[5%] text-[48px] font-medium uppercase select-none"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : 'true'}
        >
          {alt && <title>{alt}</title>}
          <text x="50%" y="50%" alignmentBaseline="middle" dominantBaseline="middle" textAnchor="middle" dy=".125em">
            {initials}
          </text>
        </svg>
      )}
      {src && <img className="size-full object-cover" src={src} alt={alt} onError={(event) => { event.currentTarget.hidden = true; }} />}
    </span>
  )
}

// Definizione dei tipi per il componente AvatarButton
type AvatarButtonAsLinkProps = AvatarProps & {
  href: string;
} & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>;

type AvatarButtonAsButtonProps = AvatarProps & {
  href?: never;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'>;

type AvatarButtonProps = AvatarButtonAsLinkProps | AvatarButtonAsButtonProps;

export const AvatarButton = forwardRef<HTMLElement, AvatarButtonProps>(function AvatarButton(
  props,
  ref
) {
  const { src, square = false, initials, alt, className, ...otherProps } = props;
  
  const classes = clsx(
    className,
    square ? 'rounded-[20%]' : 'rounded-full',
    'relative inline-grid focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500'
  );

  if ('href' in props && props.href !== undefined) {
    // È un link
    return (
      <Link 
        {...(otherProps as AvatarButtonAsLinkProps)} 
        className={classes} 
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        <TouchTarget>
          <Avatar src={src} square={square} initials={initials} alt={alt} />
        </TouchTarget>
      </Link>
    );
  } else {
    // È un pulsante
    return (
      <Headless.Button 
        {...(otherProps as AvatarButtonAsButtonProps)} 
        className={classes} 
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        type="button"
      >
        <TouchTarget>
          <Avatar src={src} square={square} initials={initials} alt={alt} />
        </TouchTarget>
      </Headless.Button>
    );
  }
});